import { useCallback, useEffect, useState } from "react";
import { patchMe } from "@/lib/api";

type Theme = "light" | "dark";

const STORAGE_KEY = "theme";

function getStoredTheme(): Theme | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(STORAGE_KEY) as Theme | null;
}

function storeTheme(theme: Theme) {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, theme);
}

export function useTheme(userDarkMode?: boolean) {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = getStoredTheme();
    if (stored) return stored;
    if (typeof userDarkMode === "boolean") return userDarkMode ? "dark" : "light";
    if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
    return "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    storeTheme(theme);
  }, [theme]);

  const setDarkMode = useCallback(
    (value: boolean) => {
      const next: Theme = value ? "dark" : "light";
      setTheme(next);
      storeTheme(next);
      // Sync to backend silently
      patchMe({ dark_mode: value }).catch(() => {
        // ignore network errors – local state already updated
      });
    },
    []
  );

  const toggle = useCallback(() => {
    setDarkMode(theme === "light");
  }, [theme, setDarkMode]);

  return { theme, isDark: theme === "dark", toggle, setDarkMode };
}
